import { dataLayer } from "@repo/analytics/data-layer-utils";
import {
  dlEventConstants,
  componentNameConstants,
  dlElementConstants,
  cleverTapJourneyNames,
  cleverTapArticleNames,
} from "@repo/analytics/data-layer.constants";

/**
 * Generic DL tracker for tab clicks in global BFLTabs component.
 *
 * @param config - Object containing DL properties
 */
export const handleTabsDL = (
  loginStatus: string,
  config: {
    sectionTitle: string;
    ctaText: string;
    tabPosition: number;
    subSection?: string;
    componentName?: string;
    elementName?: string;
    eventType?: string;
    cleverTapEventName?: string;
    cleverTapArticleName?: string;
  },
) => {
  const {
    sectionTitle,
    ctaText,
    tabPosition,
    subSection,
    componentName = "scrip_financials",
    elementName = "scrip_financials_tab_click",
    eventType = "tab_click",
    cleverTapEventName = "TRADINGMALL_DETAILS_SUBMITTED",
    cleverTapArticleName = "FINANCIALS",
  } = config;

  const data = {
    section_title: sectionTitle,
    component: componentName,
    event: dlEventConstants.CLICK,
    event_type: eventType,
    element: elementName,
    cta_text: ctaText,
    tab_position: tabPosition.toString(),
    ...(subSection && { sub_section: subSection }),
  };

  const cleverTapObj = {
    EVENT_NAME: cleverTapEventName,
    ...(cleverTapArticleName && { EP_ARTICLE_NAME: cleverTapArticleName }),
    EP_PAGE_NAME: sectionTitle,
    EP_JOURNEY_NAME: cleverTapJourneyNames.TRADING_MALL_STOCK_PDP,
    EP_SECTION: ctaText,
    EP_TYPE: dlElementConstants.STOCKS,
  };

  dataLayer(loginStatus, data, cleverTapObj);
};

/**
 * Predefined configuration for Financials tab tracking
 */
export const handleFinancialsTabDL = (
  loginStatus: string,
  config: {
    sectionTitle: string;
    ctaText: string;
    tabPosition: number;
    subSection?: string;
  },
) => {
  return handleTabsDL(loginStatus, {
    ...config,
    componentName: componentNameConstants.SCRIP_FINANCIALS,
    elementName: dlElementConstants.SCRIP_FINANCIALS_TAB_CLICK,
    eventType: dlEventConstants.TAB_CLICK,
    cleverTapEventName: dlElementConstants.PDP_CLICKED,
    cleverTapArticleName: cleverTapArticleNames.FINANCIALS,
  });
};

/**
 * Predefined configuration for Technical Summary tab tracking
 */
export const handleTechnicalSummaryTabDL = (
  loginStatus: string,
  config: {
    sectionTitle: string;
    ctaText: string;
    tabPosition: number;
    subSection?: string;
  },
) => {
  return handleTabsDL(loginStatus, {
    ...config,
    componentName: componentNameConstants.TECHNICAL_SUMMARY,
    elementName: dlElementConstants.TECHNICAL_SUMMARY_TAB_CLICK,
    eventType: dlEventConstants.TAB_CLICK,
    cleverTapEventName: dlElementConstants.PDP_CLICKED,
  });
};

/**
 * Predefined configuration for Shareholding Pattern tab tracking
 */
export const handleShareholdingPatternTabDL = (
  loginStatus: string,
  config: {
    sectionTitle: string;
    ctaText: string;
    tabPosition: number;
    subSection?: string;
  },
) => {
  return handleTabsDL(loginStatus, {
    ...config,
    componentName: componentNameConstants.SHAREHOLDING_PATTERN,
    elementName: dlElementConstants.SHAREHOLDING_PATTERN_TAB_CLICK,
    eventType: dlEventConstants.TAB_CLICK,
    cleverTapEventName: dlElementConstants.SHAREHOLDING_PATTERN_TAB_CLICK,
    // No EP_ARTICLE_NAME for shareholding pattern
  });
};

/**
 * Predefined configuration for Shareholding Pattern CTA button tracking
 */
export const handleShareholdingPatternCTADL = (
  loginStatus: string,
  config: {
    sectionTitle: string;
    ctaText: string;
    subSection?: string;
  },
) => {
  return handleTabsDL(loginStatus, {
    ...config,
    componentName: componentNameConstants.SHAREHOLDING_PATTERN,
    elementName: "shareholding_pattern_cta_click",
    eventType: dlEventConstants.BUTTON_CLICK,
    tabPosition: 1, // Default position for CTA buttons
    cleverTapEventName: dlElementConstants.PDP_CLICKED,
    cleverTapArticleName: cleverTapArticleNames.FINANCIALS,
  });
};

/**
 * Specific function for Shareholding Pattern button clicks
 * Tracks button interactions within shareholding pattern components
 */
export const handleShareholdingPatternButtonDL = (
  loginStatus: string,
  config: {
    sectionTitle: string;
    ctaText: string;
  },
) => {
  const data = {
    section_title: config.sectionTitle,
    component: componentNameConstants.SHAREHOLDING_PATTERN,
    event: dlEventConstants.CLICK,
    event_type: dlEventConstants.BUTTON_CLICK,
    element: dlElementConstants.SHAREHOLDING_PATTERN_BUTTON_CLICK,
    cta_text: config.ctaText,
  };

  const cleverTapObj = {
    EVENT_NAME: dlElementConstants.PDP_CLICKED,
    EP_ARTICLE_NAME: cleverTapArticleNames.FINANCIALS,
  };

  dataLayer(loginStatus, data, cleverTapObj);
};
